import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  message,
  Card,
  Statistic,
  Row,
  Col,
  Tooltip,
  Progress,
  Dropdown,
  Select,
  Image
} from 'antd';
import { useTranslation } from 'react-i18next';
import {
  EyeOutlined,
  ReloadOutlined,
  FlagOutlined,
  DownOutlined,
  PauseOutlined,
  PlayCircleOutlined,
  UserOutlined,
  UnorderedListOutlined,
  PictureOutlined
} from '@ant-design/icons';
import BatchActionToolbar from '../../components/common/BatchActionToolbar';
import type { ColumnsType, TableRowSelection } from 'antd/es/table/interface';
import { PlanogramTask, TaskSearchParams, TaskStatistics, Priority, TaskStatus, PauseReason } from './task';
import { TaskService } from '../../services/taskService';
import TaskDetails from './TaskDetails';
import ProductListModal from './ProductListModal';
import FeedbackModal from './FeedbackModal';
import { accounts } from '../../mock/mockAccountData';
import dayjs from 'dayjs';

interface TaskListProps {
  searchParams: TaskSearchParams;
  onSearchParamsChange: (params: TaskSearchParams) => void;
}

const TaskList: React.FC<TaskListProps> = ({ searchParams, onSearchParamsChange }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState<PlanogramTask[]>([]);
  const [total, setTotal] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const [detailsVisible, setDetailsVisible] = useState(false);
  const [currentTask, setCurrentTask] = useState<PlanogramTask | null>(null);
  const [productListVisible, setProductListVisible] = useState(false);
  const [selectedTaskForProductList, setSelectedTaskForProductList] = useState<PlanogramTask | null>(null);
  const [planogramModalVisible, setPlanogramModalVisible] = useState(false);
  const [selectedTaskForPlanogram, setSelectedTaskForPlanogram] = useState<PlanogramTask | null>(null);
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
  const [selectedTaskForFeedback, setSelectedTaskForFeedback] = useState<PlanogramTask | null>(null);

  useEffect(() => {
    loadTasks();
  }, [searchParams]);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const response = await TaskService.getTaskList(searchParams);
      if (response.success && response.data) {
        // 对任务数据进行排序
        const sortedTasks = response.data.tableData;
        
        setTasks(sortedTasks);
        setTotal(response.data.paging.totalCount);
      } else {
        message.error(response.errorMessage || t('taskManagement.list.messages.fetchFailed'));
      }
    } catch (error) {
      message.error(t('taskManagement.list.messages.networkError'));
    } finally {
      setLoading(false);
    }
  };

  // 获取优先级颜色
  const getPriorityColor = (priority: Priority): string => {
    const colorMap = {
      'High': 'red',
      'Medium': 'orange',
      'Low': 'green'
    };
    return colorMap[priority];
  };

  // 获取状态颜色
  const getStatusColor = (status: TaskStatus): string => {
    const colorMap = {
      'Not Started': 'default',
      'In Progress': 'processing',
      'Paused': 'warning',
      'Completed': 'success'
    };
    return colorMap[status];
  };

  // 表格列定义
  const columns: ColumnsType<PlanogramTask> = [
    {
      title: t('taskManagement.list.columns.taskCode'),
      dataIndex: 'taskCode',
      key: 'taskCode',
      width: 120,
      fixed: 'left',
    },
    // Removed task name column
    {
      title: t('taskManagement.list.columns.shop'),
      dataIndex: 'shop',
      key: 'shop',
      width: 120,
    },
    {
      title: t('taskManagement.search.displayGroup'),
      dataIndex: 'displayGroup',
      key: 'displayGroup',
      width: 120,
    },
    {
      title: t('taskManagement.search.division'),
      dataIndex: 'division',
      key: 'division',
      width: 100,
      render: (division: string) => (
        <Tag color={division === 'Dry Food' ? 'blue' : division === 'Fresh Food' ? 'green' : 'orange'}>
          {division}
        </Tag>
      ),
    },
    {
      title: t('taskManagement.search.shelf'),
      dataIndex: 'shelf',
      key: 'shelf',
      width: 100,
    },
        {
      title: 'Section Owner', // Section Owner
      dataIndex: 'sectionOwner',
      key: 'sectionOwner',
      width: 120,
      render: (sectionOwner: string) => (
        <span>{sectionOwner || '-'}</span>
      ),
    },
    {
      title: t('taskManagement.list.columns.isOverdue'), // 是否延期
      dataIndex: 'isOverdue',
      key: 'isOverdue',
      width: 100,
      render: (isOverdue: boolean) => (
        <Tag color={isOverdue ? 'red' : 'green'}>
          {isOverdue ? t('common.yes') : t('common.no')}
        </Tag>
      ),
    },
    {
      title: t('taskManagement.search.planogramPlan'),
      dataIndex: 'planogramPlan',
      key: 'planogramPlan',
      width: 180,
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          {text}
        </Tooltip>
      ),
    },
    {
      title: t('taskManagement.list.columns.planType'),
      dataIndex: 'planType',
      key: 'planType',
      width: 100,
    },
    {
      title: t('taskManagement.list.columns.priority'),
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority: Priority) => (
        <Tag color={getPriorityColor(priority)}>{priority}</Tag>
      ),
    },
    {
      title: t('taskManagement.list.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: TaskStatus) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      ),
    },
    {
      title: t('taskManagement.details.status.pending'), // 暂停标识
      dataIndex: 'isPaused',
      key: 'isPaused',
      width: 120,
      render: (isPaused: boolean) => (
        <Tag color={isPaused ? 'orange' : 'green'}>
          {isPaused ? t('common.yes') : t('common.no')}
        </Tag>
      ),
    },
    {
      title: t('taskManagement.list.columns.hasFeedback'),
      dataIndex: 'hasFeedback',
      key: 'hasFeedback',
      width: 100,
      render: (hasFeedback: boolean) => (
        <Tag color={hasFeedback ? 'orange' : 'green'}>
          {hasFeedback ? t('common.yes') : t('common.no')}
        </Tag>
      ),
    },
    {
      title: t('taskManagement.list.columns.feedbackCompleted'),
      dataIndex: 'feedbackCompleted',
      key: 'feedbackCompleted',
      width: 180,
      render: (feedbackCompleted: boolean | null) => (
        feedbackCompleted !== null ? (
          <Tag color={feedbackCompleted ? 'orange' : 'green'}>
            {feedbackCompleted ? t('common.yes') : t('common.no')}
          </Tag>
        ) : (
          <span>-</span>
        )
      ),
    },
    
    {
      title: t('taskManagement.list.columns.pauseReason'),
      dataIndex: 'pauseReason',
      key: 'pauseReason',
      width: 160,
      render: (pauseReason: PauseReason | undefined, record: PlanogramTask) => {
        if (record.isPaused && pauseReason) {
          const reasonKey = pauseReason.toLowerCase().replace(/\s+/g, '');
          return <Tag color="orange">{t(`taskManagement.pauseReasons.${reasonKey}`)}</Tag>;
        }
        return '-';
      },
    },
    {
      title: t('taskManagement.list.pauseModal.pauseRemark'),
      dataIndex: 'pauseRemark',
      key: 'pauseRemark',
      width: 160,
    },
    {
      title: t('taskManagement.details.tabs.moveOut') + " " + t('common.progress'), // 下架进度
      dataIndex: 'moveOutProgress',
      key: 'moveOutProgress',
      width: 120,
      render: (progress: any) => (
        <div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {progress.actualSkuCount}/{progress.plannedSkuCount}
          </div>
          <Progress 
            percent={progress.plannedSkuCount > 0 ? Math.round((progress.actualSkuCount / progress.plannedSkuCount) * 100) : 0} 
            size="small" 
            showInfo={false}
          />
        </div>
      ),
    },
    {
      title: t('taskManagement.details.tabs.moveIn') + " " + t('common.progress'), // 移入进度
      dataIndex: 'moveInProgress',
      key: 'moveInProgress',
      width: 120,
      render: (progress: any) => (
        <div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {progress.actualSkuCount}/{progress.plannedSkuCount}
          </div>
          <Progress 
            percent={progress.plannedSkuCount > 0 ? Math.round((progress.actualSkuCount / progress.plannedSkuCount) * 100) : 0} 
            size="small" 
            showInfo={false}
          />
        </div>
      ),
    },
    {
      title: t('taskManagement.details.tabs.rearrange') + " "+ t('common.progress'), // 调整进度
      dataIndex: 'rearrangeProgress',
      key: 'rearrangeProgress', 
      width: 120,
      render: (progress: any) => (
        <div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {progress.actualSkuCount}/{progress.plannedSkuCount}
          </div>
          <Progress 
            percent={progress.plannedSkuCount > 0 ? Math.round((progress.actualSkuCount / progress.plannedSkuCount) * 100) : 0} 
            size="small" 
            showInfo={false}
          />
        </div>
      ),
    },
    {
      title: t('taskManagement.details.tabs.newItem') + " "+ t('common.progress'), // 新商品进度
      dataIndex: 'newItemProgress',
      key: 'newItemProgress',
      width: 120,
      render: (progress: any) => (
        <div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {progress.actualSkuCount}/{progress.plannedSkuCount}
          </div>
          <Progress 
            percent={progress.plannedSkuCount > 0 ? Math.round((progress.actualSkuCount / progress.plannedSkuCount) * 100) : 0} 
            size="small" 
            showInfo={false}
          />
        </div>
      ),
    },
    {
      title: t('common.totalItems'), // 待处理商品总件数
      dataIndex: 'totalItemsCount',
      key: 'totalItemsCount',
      width: 140,
      render: (count: number) => (
        <span style={{ fontWeight: 'bold' }}>{count}</span>
      ),
    },
    // {
    //   title: t('common.totalWeight'), // 待处理商品总重量
    //   dataIndex: 'totalWeight',
    //   key: 'totalWeight',
    //   width: 140,
    //   render: (weight: number) => (
    //     <span style={{ fontWeight: 'bold' }}>{weight.toFixed(2)} kg</span>
    //   ),
    // },
    {
      title: t('taskManagement.list.columns.planStartDate'),
      dataIndex: 'planStartDate',
      key: 'planStartDate',
      width: 120,
      render: (timestamp: number) => formatDateTime(timestamp),
    },
    {
      title: t('taskManagement.list.columns.planEndDate'),
      dataIndex: 'planEndDate',
      key: 'planEndDate',
      width: 120,
      render: (timestamp: number) => formatDateTime(timestamp),
    },
    {
      title: t('taskManagement.list.columns.generateTime'),
      dataIndex: 'generateTime',
      key: 'generateTime',
      width: 180,
      render: (timestamp: number) => formatDateTime(timestamp),
    },
    {
      title: t('taskManagement.list.columns.completeTime'),
      dataIndex: 'completeTime',
      key: 'completeTime',
      width: 180,
      render: (timestamp: number) => formatDateTime(timestamp),
    },

    // {
    //   title: t('common.createBy'), // 创建人
    //   dataIndex: 'createBy',
    //   key: 'createBy',
    //   width: 100,
    // },
    {
      title: t('taskManagement.list.columns.actions'),
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t('taskManagement.list.actions.view')}>
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Tooltip title={t('taskManagement.list.actions.productList')}>
            <Button
              type="text"
              size="small"
              icon={<UnorderedListOutlined />}
              onClick={() => handleViewProductList(record)}
            />
          </Tooltip>
          <Tooltip title={t('taskManagement.list.actions.viewPlanogram')}>
            <Button
              type="text"
              size="small"
              icon={<PictureOutlined />}
              onClick={() => handleViewPlanogram(record)}
            />
          </Tooltip>
          {record.hasFeedback && ( // 只有当任务有feedback时才显示查看feedback按钮
            <Tooltip title={t('taskManagement.list.actions.viewFeedback')}>
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleViewFeedback(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // 处理分页变化
  const handleTableChange = (pagination: any) => {
    const newParams = {
      ...searchParams,
      currentPage: pagination.current,
      pageSize: pagination.pageSize,
    };
    onSearchParamsChange(newParams);
  };

  // 查看详情
  const handleViewDetails = (task: PlanogramTask) => {
    setCurrentTask(task);
    setDetailsVisible(true);
  };

  // 查看商品清单
  const handleViewProductList = (task: PlanogramTask) => {
    setSelectedTaskForProductList(task);
    setProductListVisible(true);
  };

  // 查看陈列图
  const handleViewPlanogram = (task: PlanogramTask) => {
    setSelectedTaskForPlanogram(task);
    setPlanogramModalVisible(true);
  };

  // 查看feedback
  const handleViewFeedback = (task: PlanogramTask) => {
    setSelectedTaskForFeedback(task);
    setFeedbackModalVisible(true);
  };

  // 行选择配置
  const rowSelection: TableRowSelection<PlanogramTask> = {
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(selectedRowKeys);
    },
    getCheckboxProps: (record: PlanogramTask) => ({
      disabled: record.status === 'Completed', // 已完成的任务不能选择
    }),
  };

    const formatDateTime = (timestamp: number): string => {
      if (!timestamp) {
        return '';
      }
      return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
    };

  return (
    <div>
      {/* 任务列表 */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
          {/* <BatchActionToolbar
            selectedCount={selectedRowKeys.length}
            actions={[
              {
                key: 'priorityHigh',
                label: t('taskManagement.list.batchActions.highPriority'),
                icon: <FlagOutlined />,
                onClick: () => handleBatchPriorityMark('High'),
              },
              {
                key: 'priorityMedium',
                label: t('taskManagement.priority.medium'),
                icon: <FlagOutlined />,
                onClick: () => handleBatchPriorityMark('Medium'),
              },
              {
                key: 'priorityLow',
                label: t('taskManagement.list.batchActions.lowPriority'),
                icon: <FlagOutlined />,
                onClick: () => handleBatchPriorityMark('Low'),
              },
              {
                key: 'pause',
                label: t('taskManagement.list.batchActions.batchPause'),
                icon: <PauseOutlined />,
                onClick: handleBatchPause,
              },
              {
                key: 'resume',
                label: t('taskManagement.list.batchActions.batchResume'),
                icon: <PlayCircleOutlined />,
                onClick: handleBatchResume,
              },
              {
                key: 'batchAssign',
                label: t('taskManagement.list.batchActions.batchAssign'),
                icon: <UserOutlined />,
                onClick: handleBatchAssign,
              }
            ]}
            onClearSelection={() => setSelectedRowKeys([])}
          /> */}
        </div>
        <Table
          columns={columns}
          dataSource={tasks}
          rowKey="taskCode"
          loading={loading}
          rowSelection={rowSelection}
          scroll={{ x: 1200, y: 500 }}
          pagination={{
            current: searchParams.currentPage,
            pageSize: searchParams.pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => t('taskManagement.list.messages.paginationTotal', { start: range[0], end: range[1], total }),
            pageSizeOptions: ['20', '50', '100'],
          }}
          onChange={handleTableChange}
        />
      </Card>



      {/* 任务详情弹窗 */}
      <Modal
        title={t('taskManagement.list.messages.taskDetailsTitle')}
        open={detailsVisible}
        onCancel={() => setDetailsVisible(false)}
        width={1200}
        footer={null}
        destroyOnClose={true}
      >
        {currentTask && (
          <TaskDetails key={currentTask.taskCode} task={currentTask} />
        )}
      </Modal>

      {/* 商品清单弹窗 */}
      <ProductListModal
        visible={productListVisible}
        onClose={() => setProductListVisible(false)}
        task={selectedTaskForProductList}
      />


      {/* 查看陈列图对话框 */}
      <Modal
        title={t('taskManagement.list.planogramModal.title')}
        open={planogramModalVisible}
        onCancel={() => {
          setSelectedTaskForPlanogram(null);
          setPlanogramModalVisible(false);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setSelectedTaskForPlanogram(null);
            setPlanogramModalVisible(false);
          }}>
            {t('common.close')}
          </Button>
        ]}
        width={800}
        destroyOnClose={true}
      >
        {planogramModalVisible && selectedTaskForPlanogram && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Image
              key={selectedTaskForPlanogram.taskCode}
              src={selectedTaskForPlanogram.shelfPicture}
              style={{ width: '100%', height: 550, objectFit: 'cover', border: '1px solid #d9d9d9' }}
              placeholder
            />
            <p style={{ fontSize: '14px', color: '#666', marginTop: '16px' }}>
              {t('taskManagement.list.planogramModal.taskCode')}: {selectedTaskForPlanogram.taskCode}
            </p>
          </div>
        )}
      </Modal>

      {/* 查看Feedback对话框 */}
      <FeedbackModal
        visible={feedbackModalVisible}
        onClose={() => {
          setFeedbackModalVisible(false);
          setSelectedTaskForFeedback(null);
        }}
        task={selectedTaskForFeedback}
      />
    </div>
  );
};

export default TaskList;